import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sparkles, Calendar as CalendarIcon, Plus, Trash2, Film, Image as ImageIcon, Layers, Wand2, PlayCircle, RefreshCw, Heart, CheckCircle2, Loader2, Send, Grid3X3, Eye, AlertTriangle, Shield, ShieldCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  reel:     { label: "Reel",     icon: Film,        color: "#D97757", bg: "#FBE6DC" },
  post:     { label: "Post",     icon: ImageIcon,   color: "#7A9180", bg: "#E2EAE2" },
  carousel: { label: "Carrusel", icon: Layers,      color: "#A69076", bg: "#F1E9DE" },
};

const WEEKDAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function defaultMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function MonthlyPanel() {
  const [campaigns, setCampaigns] = useState([]);
  const [palettes, setPalettes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ month: defaultMonth(), service_focus: "general", palette: "" });
  const [seeding, setSeeding] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [view, setView] = useState("calendar"); // 'calendar' | 'feed' | 'eval'
  const [feedItems, setFeedItems] = useState([]);
  const [evaluation, setEvaluation] = useState(null);
  const [evalLoading, setEvalLoading] = useState(false);

  const loadFeed = async (id) => {
    if (!id) return;
    try { const r = await api.get(`/monthly/campaigns/${id}/feed-mockup`); setFeedItems(r.data.items || []); }
    catch (_) { setFeedItems([]); }
  };

  const runEvaluation = async () => {
    if (!active) return;
    setEvalLoading(true);
    setEvaluation(null);
    try {
      const r = await api.post(`/monthly/campaigns/${active.id}/evaluate-feed`);
      setEvaluation(r.data);
      toast.success(`Evaluación lista · ${r.data.overall_score}/10`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al evaluar");
    } finally { setEvalLoading(false); }
  };

  const autoFixFeed = async () => {
    if (!active || !evaluation) return;
    const n = (evaluation.weak_pieces || []).length;
    if (n === 0) { toast.info("No hay piezas débiles que corregir"); return; }
    if (!confirm(`Voy a regenerar ${n} pieza(s) marcada(s) como problemáticas, aplicando las recomendaciones de la IA. Esto puede tomar 1-3 minutos. ¿Continuar?`)) return;
    try {
      const r = await api.post(`/monthly/campaigns/${active.id}/auto-fix-feed`);
      const f = r.data.fixed;
      toast.success(`Regenerando: ${f.post}P + ${f.carousel}C + ${f.reel}R`);
      setEvaluation(null);
      await loadActive(active.id);
      await loadFeed(active.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al auto-corregir");
    }
  };

  const loadAll = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/monthly/campaigns"),
        api.get("/brand/palettes"),
      ]);
      setCampaigns(a.data || []);
      setPalettes(b.data?.palettes || []);
      if (!activeId && a.data?.length) {
        setActiveId(a.data[0].id);
      }
    } catch (_) { /* noop */ }
  };
  const loadActive = async (id) => {
    if (!id) { setActive(null); return; }
    try {
      const r = await api.get(`/monthly/campaigns/${id}`);
      setActive(r.data);
    } catch (_) { setActive(null); }
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadActive(activeId); }, [activeId]);

  // Auto-poll while any slot is generating
  useEffect(() => {
    if (!active) return;
    const anyGen = (active.slots || []).some((s) => s.status === "generating");
    if (!anyGen) return;
    const t = setInterval(() => loadActive(active.id), 6000);
    return () => clearInterval(t);
  }, [active]);

  useEffect(() => {
    if (active?.id && (view === "feed" || view === "eval")) {
      loadFeed(active.id);
      if (view === "eval" && active?.last_feed_evaluation && !evaluation) {
        setEvaluation(active.last_feed_evaluation);
      }
    }
  }, [view, active?.id]);

  const createCampaign = async () => {
    try {
      const r = await api.post("/monthly/campaigns", {
        month: form.month,
        service_focus: form.service_focus,
        palette: form.palette || null,
      });
      toast.success("Campaña creada");
      setCreateOpen(false);
      setActiveId(r.data.id);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear");
    }
  };

  const seedNow = async () => {
    if (!active) return;
    setSeeding(true);
    try {
      const r = await api.post(`/monthly/campaigns/${active.id}/seed`);
      toast.success(`Temas generados: ${r.data.filled.reels}R + ${r.data.filled.posts}P + ${r.data.filled.carousels}C + ${r.data.filled.stories} stories`);
      await loadActive(active.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al sembrar");
    } finally { setSeeding(false); }
  };

  const generateAll = async () => {
    if (!active) return;
    try {
      const r = await api.post(`/monthly/campaigns/${active.id}/generate-all`);
      toast.success(`Encolado: ${r.data.queued.reel}R · ${r.data.queued.post}P · ${r.data.queued.carousel}C`);
      await loadActive(active.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar");
    }
  };

  const applyWomenMonth = async () => {
    if (!active) return;
    if (!active.month?.endsWith("-03")) {
      toast.error("Esta playbook es solo para campañas de Marzo");
      return;
    }
    if (!confirm("Esto reemplazará el contenido del 1, 8, 15, 29 con las plantillas editoriales (Prada Pink + Cover Neón) y marca los reels del 7 y 9 como rosa monocromático. ¿Continuar?")) return;
    try {
      const r = await api.post(`/monthly/campaigns/${active.id}/apply-women-month`);
      toast.success(`Mes de la Mujer aplicado · ${r.data.count} piezas editoriales`);
      await loadActive(active.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aplicar");
    }
  };

  const generateSlot = async (slotId) => {
    try {
      await api.post(`/monthly/campaigns/${active.id}/slots/${slotId}/generate`);
      toast.success("Slot encolado");
      await loadActive(active.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const saveSlot = async () => {
    if (!editingSlot) return;
    try {
      // 1) Save slot meta (topic/time/notes)
      await api.patch(`/monthly/campaigns/${active.id}/slots/${editingSlot.slot_id}`, {
        topic: editingSlot.topic,
        scheduled_time: editingSlot.scheduled_time,
        notes: editingSlot.notes,
      });
      // 2) If the slot has generated content and the caption was edited, persist
      if (editingSlot.generated_id && editingSlot.caption_preview !== undefined && editingSlot._caption_dirty) {
        await api.patch(`/monthly/campaigns/${active.id}/slots/${editingSlot.slot_id}/child`, {
          caption: editingSlot.caption_preview,
        });
      }
      toast.success("Cambios guardados");
      setEditingSlot(null);
      await loadActive(active.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const approveSlot = async (slot) => {
    try {
      const r = await api.post(`/monthly/campaigns/${active.id}/slots/${slot.slot_id}/approve`, {});
      toast.success("Aprobado y enviado al calendario de publicación");
      setEditingSlot(null);
      await loadActive(active.id);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aprobar");
    }
  };

  const updateCampaign = async (patch) => {
    try {
      await api.patch(`/monthly/campaigns/${active.id}`, patch);
      await loadActive(active.id);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const removeCampaign = async () => {
    if (!confirm("¿Eliminar esta campaña? Los posts y reels generados se conservan.")) return;
    await api.delete(`/monthly/campaigns/${active.id}`);
    setActiveId(null);
    await loadAll();
  };

  // ----- build calendar grid -----
  const buildGrid = () => {
    if (!active) return [];
    const [y, m] = active.month.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    // Pad start with empty cells before weekday-0 (Mon) of week
    const startPad = (first.getDay() + 6) % 7; // Mon=0
    const total = new Date(y, m, 0).getDate();
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= total; d++) {
      const date = `${active.month}-${String(d).padStart(2, "0")}`;
      const slots = active.slots.filter((s) => s.day === d);
      cells.push({ day: d, date, slots });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  };

  const grid = buildGrid();
  const activePalette = palettes.find((p) => p.id === active?.palette);

  return (
    <div className="space-y-6" data-testid="monthly-panel">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end gap-4 justify-between">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Calendario · Programación</div>
          <h2 className="font-display text-3xl text-[#1A1C1A]">Campañas mensuales</h2>
          <p className="text-xs text-[#5C5E5C] mt-1 max-w-2xl">
            Crea un mes completo de contenido. La IA siembra 4 Reels + 4 Posts + 2 Carruseles por semana, con voz humana mexicana y la paleta de tu marca.
          </p>
        </div>
        <div className="flex gap-2">
          {campaigns.length > 0 && (
            <Select value={activeId || ""} onValueChange={setActiveId}>
              <SelectTrigger className="w-72" data-testid="monthly-select-campaign">
                <SelectValue placeholder="Elige una campaña" />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.month} · {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white text-xs uppercase tracking-[0.2em]" data-testid="monthly-create">
                <Plus className="w-4 h-4 mr-1" /> Nueva campaña
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nueva campaña mensual</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Mes</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Select
                      value={form.month.split("-")[1] || ""}
                      onValueChange={(mm) => {
                        const yyyy = form.month.split("-")[0] || `${new Date().getFullYear()}`;
                        setForm({ ...form, month: `${yyyy}-${mm}` });
                      }}
                    >
                      <SelectTrigger data-testid="monthly-form-month-name"><SelectValue placeholder="Mes" /></SelectTrigger>
                      <SelectContent>
                        {[
                          ["01","Enero"],["02","Febrero"],["03","Marzo"],["04","Abril"],
                          ["05","Mayo"],["06","Junio"],["07","Julio"],["08","Agosto"],
                          ["09","Septiembre"],["10","Octubre"],["11","Noviembre"],["12","Diciembre"],
                        ].map(([v,l]) => (<SelectItem key={v} value={v}>{l}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={form.month.split("-")[0] || ""}
                      onValueChange={(yy) => {
                        const mm = form.month.split("-")[1] || `${String(new Date().getMonth() + 1).padStart(2,"0")}`;
                        setForm({ ...form, month: `${yy}-${mm}` });
                      }}
                    >
                      <SelectTrigger data-testid="monthly-form-month-year"><SelectValue placeholder="Año" /></SelectTrigger>
                      <SelectContent>
                        {[0,1,2].map(o => {
                          const y = `${new Date().getFullYear() + o}`;
                          return (<SelectItem key={y} value={y}>{y}</SelectItem>);
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#A69076] mt-1.5">{form.month}</div>
                </div>
                <div>
                  <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Servicio principal</Label>
                  <Select value={form.service_focus} onValueChange={(v) => setForm({ ...form, service_focus: v })}>
                    <SelectTrigger data-testid="monthly-form-service"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General · Ortodoncia + Estética</SelectItem>
                      <SelectItem value="ortodoncia">Ortodoncia</SelectItem>
                      <SelectItem value="estetica">Medicina Estética</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Paleta · Mood (opcional — si lo dejas vacío usaremos la del mes)</Label>
                  <Select value={form.palette || "auto"} onValueChange={(v) => setForm({ ...form, palette: v === "auto" ? "" : v })}>
                    <SelectTrigger data-testid="monthly-form-palette"><SelectValue placeholder="Auto (según mes)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto · según mes</SelectItem>
                      {palettes.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          <span className="flex items-center gap-2">
                            <span className="flex gap-0.5">
                              {(p.colors || []).slice(0, 4).map((c, i) => (
                                <span key={i} className="w-2.5 h-2.5 rounded-full border border-black/10" style={{ background: c }} />
                              ))}
                            </span>
                            {p.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={createCampaign} className="w-full rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white text-xs uppercase tracking-[0.2em]" data-testid="monthly-form-submit">
                  <Sparkles className="w-4 h-4 mr-2" /> Crear campaña
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {!active && (
        <div className="bg-[#F4F0EB] border border-[#E6E2DC] rounded-2xl p-10 text-center">
          <CalendarIcon className="w-10 h-10 mx-auto text-[#A69076] mb-3" />
          <p className="font-display text-2xl text-[#1A1C1A] mb-2">Aún no hay campañas</p>
          <p className="text-sm text-[#5C5E5C] mb-5">Crea tu primera campaña mensual y la IA llenará 40 piezas de contenido en segundos.</p>
        </div>
      )}

      {active && (
        <>
          {/* Campaign meta + actions */}
          <div className="bg-white rounded-2xl border border-[#E6E2DC] p-6">
            <div className="grid lg:grid-cols-12 gap-4 items-center">
              <div className="lg:col-span-5">
                <Input value={active.name} onChange={(e) => setActive({ ...active, name: e.target.value })} onBlur={(e) => updateCampaign({ name: e.target.value })} className="font-display text-xl border-0 px-0 h-auto" data-testid="monthly-name" />
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mt-1">{active.month} · {active.season_theme}</div>
              </div>
              <div className="lg:col-span-3">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-1.5 block">Paleta</Label>
                <Select value={active.palette} onValueChange={(v) => updateCampaign({ palette: v })}>
                  <SelectTrigger data-testid="monthly-palette"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {palettes.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        <span className="flex items-center gap-2">
                          <span className="flex gap-0.5">
                            {(p.colors || []).slice(0, 4).map((c, i) => (
                              <span key={i} className="w-2.5 h-2.5 rounded-full border border-black/10" style={{ background: c }} />
                            ))}
                          </span>
                          {p.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="lg:col-span-4 flex flex-col items-end gap-2">
                <div className="flex items-center gap-2 text-[11px]">
                  {active.guardian_mode ? (
                    <ShieldCheck className="w-3.5 h-3.5 text-[#7A9180]" />
                  ) : (
                    <Shield className="w-3.5 h-3.5 text-[#A69076]" />
                  )}
                  <span className="text-[#5C5E5C]">Modo guardián</span>
                  <Switch
                    checked={!!active.guardian_mode}
                    onCheckedChange={(v) => updateCampaign({ guardian_mode: v })}
                    data-testid="monthly-guardian-toggle"
                  />
                  {active.guardian_running && (
                    <span className="flex items-center gap-1 text-[10px] text-[#7A9180] uppercase tracking-widest">
                      <Loader2 className="w-3 h-3 animate-spin" /> Vigilando
                    </span>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                  {active.month?.endsWith("-03") && (
                    <Button size="sm" variant="outline" onClick={applyWomenMonth} className="border-[#E8A8A8] text-[#C04A6E] hover:bg-[#FBE0DD]" data-testid="monthly-apply-women-month">
                      <Heart className="w-4 h-4 mr-1" /> Aplicar Mes de la Mujer
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={seedNow} disabled={seeding} data-testid="monthly-seed">
                    <Wand2 className="w-4 h-4 mr-1" /> {seeding ? "Sembrando..." : "Sembrar temas con IA"}
                  </Button>
                  <Button size="sm" onClick={generateAll} className="bg-[#A69076] hover:bg-[#1A1C1A] text-white" data-testid="monthly-generate-all">
                    <PlayCircle className="w-4 h-4 mr-1" /> Generar todo
                  </Button>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-[#5C5E5C]">
                  {Object.entries(active.counts || {}).map(([k, v]) => {
                    const meta = TYPE_META[k];
                    if (!meta) return null;
                    const Icon = meta.icon;
                    return (
                      <span key={k} className="flex items-center gap-1">
                        <Icon className="w-3 h-3" style={{ color: meta.color }} /> {v} {meta.label.toLowerCase()}s
                      </span>
                    );
                  })}
                  <button onClick={removeCampaign} className="text-[#D97757] hover:underline" data-testid="monthly-delete">Eliminar</button>
                </div>
              </div>
            </div>
            {activePalette && (
              <div className="mt-4 flex items-center gap-3 text-[11px] text-[#5C5E5C]">
                <span className="flex gap-0.5">
                  {activePalette.colors.slice(0, 6).map((c, i) => (
                    <span key={i} className="w-4 h-4 rounded-md border border-black/10" style={{ background: c }} />
                  ))}
                </span>
                <span>{activePalette.tagline}</span>
              </div>
            )}
          </div>

          {/* View toggle */}
          <div className="flex justify-center">
            <div className="inline-flex rounded-full bg-[#F4F0EB] p-1 border border-[#E6E2DC]">
              <button onClick={() => setView("calendar")} className={`flex items-center gap-1.5 px-4 h-8 rounded-full text-[10px] uppercase tracking-widest transition-colors ${view === "calendar" ? "bg-[#1A1C1A] text-white" : "text-[#5C5E5C]"}`} data-testid="monthly-view-calendar">
                <CalendarIcon className="w-3 h-3" /> Calendario
              </button>
              <button onClick={() => setView("feed")} className={`flex items-center gap-1.5 px-4 h-8 rounded-full text-[10px] uppercase tracking-widest transition-colors ${view === "feed" ? "bg-[#1A1C1A] text-white" : "text-[#5C5E5C]"}`} data-testid="monthly-view-feed">
                <Grid3X3 className="w-3 h-3" /> Feed mockup
              </button>
              <button onClick={() => setView("eval")} className={`flex items-center gap-1.5 px-4 h-8 rounded-full text-[10px] uppercase tracking-widest transition-colors ${view === "eval" ? "bg-[#1A1C1A] text-white" : "text-[#5C5E5C]"}`} data-testid="monthly-view-eval">
                <Eye className="w-3 h-3" /> Evaluación IA
              </button>
            </div>
          </div>

          {/* Calendar grid */}
          {view === "calendar" && (
          <div className="bg-white rounded-2xl border border-[#E6E2DC] p-4 overflow-x-auto" data-testid="monthly-calendar">
            <div className="grid grid-cols-7 gap-2 min-w-[760px]">
              {WEEKDAY_LABELS.map((d) => (
                <div key={d} className="text-[10px] tracking-[0.2em] uppercase text-[#A69076] font-medium text-center py-2">{d}</div>
              ))}
              {grid.map((cell, i) => {
                if (!cell) return <div key={i} className="min-h-[110px]" />;
                return (
                  <div key={i} className="border border-[#EFEAE2] rounded-lg p-2 min-h-[110px] flex flex-col gap-1 bg-[#FAFAF7]" data-testid={`monthly-day-${cell.day}`}>
                    <div className="text-[11px] text-[#5C5E5C] font-medium">{cell.day}</div>
                    {cell.slots.map((s) => {
                      const meta = TYPE_META[s.type];
                      const Icon = meta?.icon || ImageIcon;
                      const isGen = s.status === "generating";
                      const isReady = s.status === "ready";
                      const isSched = s.status === "scheduled";
                      const isFailed = s.status === "failed";
                      // Resolve thumbnail src
                      let thumbSrc = null;
                      if (s.type === "reel" && s.thumb_url) {
                        thumbSrc = s.thumb_url.startsWith("http") ? s.thumb_url : `${BACKEND}${s.thumb_url}`;
                      } else if (s.thumb_inline) {
                        thumbSrc = s.thumb_inline.startsWith("data:") ? s.thumb_inline : `data:image/png;base64,${s.thumb_inline}`;
                      } else if (s.generated_id && (s.type === "post" || s.type === "carousel")) {
                        const idx = s.type === "carousel" ? "/slide/0" : "";
                        thumbSrc = `${BACKEND}/api/public/media/social/${s.generated_id}${idx}`;
                      }
                      return (
                        <button
                          key={s.slot_id}
                          onClick={() => setEditingSlot(s)}
                          className={`group relative overflow-hidden text-left rounded-md hover:ring-2 hover:ring-[#A69076]/60 transition ${s.editorial_style ? "ring-2 ring-[#FF2D95]/40" : ""}`}
                          style={{ background: s.editorial_style === "prada_pink" ? "#FBE0DD" : s.editorial_style === "neon_contour" ? "#FDD3E4" : s.editorial_style === "monochrome_pink" ? "#FCE7E3" : (meta?.bg || "#F4F0EB") }}
                          title={s.topic || "(sin tema)"}
                          data-testid={`monthly-slot-${s.slot_id}`}
                        >
                          {/* Thumbnail */}
                          {thumbSrc && (
                            <div className="w-full aspect-square bg-[#EFEAE2] overflow-hidden">
                              <img src={thumbSrc} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            </div>
                          )}
                          <div className="px-1.5 py-1">
                            <div className="flex items-center gap-1">
                              <Icon className="w-3 h-3 flex-shrink-0" style={{ color: meta?.color }} />
                              <span className="text-[10px] font-medium text-[#1A1C1A]">{s.scheduled_time}</span>
                              {s.editorial_style && <Heart className="w-2.5 h-2.5 ml-0.5 text-[#FF2D95] fill-[#FF2D95]" />}
                              <span className="ml-auto flex items-center gap-1">
                                {isGen && <Loader2 className="w-3 h-3 text-[#A69076] animate-spin" />}
                                {isReady && <span className="text-[8px] text-[#7A9180] font-bold tracking-wider">LISTO</span>}
                                {isSched && <span className="text-[8px] text-[#1A1C1A] font-bold tracking-wider">📅</span>}
                                {isFailed && <span className="text-[8px] text-[#D97757] font-bold tracking-wider">ERR</span>}
                              </span>
                            </div>
                            <div className="text-[10px] text-[#3C3E3C] line-clamp-2 mt-0.5">
                              {s.topic || <span className="italic text-[#A69076]">sin tema</span>}
                            </div>
                            {isGen && (
                              <div className="mt-1 h-1 bg-[#EFEAE2] rounded-full overflow-hidden">
                                <div className="h-full bg-[#A69076] transition-all" style={{ width: `${s.progress || 30}%` }} />
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
          )}

          {/* Feed mockup view — IG profile grid 3×N */}
          {view === "feed" && (
            <div className="bg-white rounded-2xl border border-[#E6E2DC] p-6" data-testid="monthly-feed-mockup">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Vista previa del feed de Instagram</div>
                  <div className="text-xs text-[#5C5E5C]">Las publicaciones más recientes aparecen arriba-izquierda · {feedItems.length} piezas generadas</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => loadFeed(active.id)} data-testid="monthly-feed-refresh">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualizar
                </Button>
              </div>
              {feedItems.length === 0 ? (
                <div className="py-16 text-center text-sm text-[#A69076]">
                  Aún no hay piezas generadas. Sembra temas y genera contenido para ver el mockup.
                </div>
              ) : (
                <div className="max-w-md mx-auto bg-[#FAFAF7] border border-[#E6E2DC] rounded-lg p-2">
                  <div className="grid grid-cols-3 gap-0.5">
                    {feedItems.map((it) => {
                      const thumb = it.thumb_inline
                        ? (it.thumb_inline.startsWith("data:") ? it.thumb_inline : `data:image/png;base64,${it.thumb_inline}`)
                        : (it.thumb_url ? (it.thumb_url.startsWith("http") ? it.thumb_url : `${BACKEND}${it.thumb_url}`) : null);
                      const TypeIcon = TYPE_META[it.type]?.icon || ImageIcon;
                      return (
                        <button
                          key={it.slot_id}
                          onClick={() => {
                            const slot = active.slots.find((s) => s.slot_id === it.slot_id);
                            if (slot) setEditingSlot(slot);
                          }}
                          className="relative aspect-square overflow-hidden bg-[#EFEAE2] group hover:opacity-80 transition"
                          data-testid={`feed-item-${it.slot_id}`}
                        >
                          {thumb && <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />}
                          <div className="absolute top-1 right-1 bg-black/60 rounded-full p-1 opacity-70 group-hover:opacity-100">
                            <TypeIcon className="w-3 h-3 text-white" />
                          </div>
                          {it.editorial_style && (
                            <div className="absolute top-1 left-1 bg-[#FF2D95] rounded-full p-1">
                              <Heart className="w-2.5 h-2.5 text-white fill-white" />
                            </div>
                          )}
                          {it.slides_count > 1 && (
                            <div className="absolute bottom-1 right-1 bg-black/60 rounded px-1.5 py-0.5 text-[9px] text-white font-bold">
                              {it.slides_count}↩
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* AI evaluation view */}
          {view === "eval" && (
            <div className="space-y-4" data-testid="monthly-eval-view">
              <div className="bg-white rounded-2xl border border-[#E6E2DC] p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Evaluación visual con IA</div>
                    <h3 className="font-display text-2xl text-[#1A1C1A]">¿Tu feed se ve coherente?</h3>
                    <p className="text-xs text-[#5C5E5C] mt-1 max-w-xl">Directora de arte virtual revisa cada pieza y te dice qué no encaja con tu paleta + sugiere reacomodo inteligente.</p>
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    <Button onClick={runEvaluation} disabled={evalLoading || feedItems.length === 0} className="bg-[#1A1C1A] hover:bg-[#A69076] text-white" data-testid="monthly-evaluate">
                      {evalLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                      {evalLoading ? "Evaluando…" : evaluation ? "Re-evaluar" : "Evaluar feed"}
                    </Button>
                    {evaluation?.weak_pieces?.length > 0 && (
                      <Button onClick={autoFixFeed} variant="outline" className="border-[#D97757] text-[#D97757] hover:bg-[#FBE6DC]" data-testid="monthly-auto-fix">
                        <Wand2 className="w-4 h-4 mr-1" /> Auto-corregir ({evaluation.weak_pieces.length})
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {evaluation && (
                <>
                  {/* Score + overall diagnosis */}
                  <div className="bg-white rounded-2xl border border-[#E6E2DC] p-6 flex flex-col sm:flex-row items-center gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-28 h-28 rounded-full border-4 border-[#A69076] flex items-center justify-center bg-[#F4F0EB]">
                        <div className="text-center">
                          <div className="font-display text-4xl text-[#1A1C1A]">{evaluation.overall_score}</div>
                          <div className="text-[9px] tracking-[0.25em] uppercase text-[#A69076]">/ 10</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Diagnóstico</div>
                      <p className="text-sm text-[#1A1C1A] leading-relaxed">{evaluation.overall_diagnosis}</p>
                      {evaluation.top_tip && (
                        <div className="mt-3 bg-[#FBE6DC] rounded-lg px-3 py-2 text-[12px] text-[#3C3E3C] border border-[#F2C7AE]">
                          <strong className="text-[#D97757]">Tip:</strong> {evaluation.top_tip}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Weak pieces */}
                  {evaluation.weak_pieces?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-[#F2C7AE] p-5" data-testid="monthly-weak-pieces">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-[#D97757]" />
                        <div className="text-[10px] tracking-[0.25em] uppercase text-[#D97757]">Piezas que NO encajan ({evaluation.weak_pieces.length})</div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
                        {evaluation.pieces.filter((p) => evaluation.weak_pieces.includes(p.index)).map((p) => {
                          const thumb = p.thumb_url ? (p.thumb_url.startsWith("http") ? p.thumb_url : `${BACKEND}${p.thumb_url}`) : null;
                          return (
                            <button
                              key={p.slot_id}
                              onClick={() => {
                                const slot = active.slots.find((s) => s.slot_id === p.slot_id);
                                if (slot) setEditingSlot(slot);
                              }}
                              className="text-left aspect-square relative overflow-hidden rounded-md ring-2 ring-[#D97757] hover:ring-4 transition"
                              data-testid={`weak-piece-${p.slot_id}`}
                              title={p.reason}
                            >
                              {thumb ? (
                                <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                              ) : (
                                <div className="w-full h-full bg-[#F4F0EB] flex items-center justify-center text-[#A69076] text-xs">{p.date}</div>
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                              <div className="absolute bottom-1 left-1 right-1 text-[9px] text-white leading-tight">
                                <span className="font-bold">{p.score}/10</span> · {p.reason}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Rearrangement suggestions */}
                  {evaluation.rearrangement?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-[#E6E2DC] p-5" data-testid="monthly-rearrangement">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-3">Reacomodo sugerido por IA</div>
                      <div className="space-y-2">
                        {evaluation.rearrangement.map((r, i) => (
                          <div key={i} className="flex items-center gap-3 bg-[#FAFAF7] rounded-lg px-3 py-2 text-xs">
                            <Send className="w-3.5 h-3.5 text-[#A69076] flex-shrink-0" />
                            <span className="text-[#1A1C1A]">Pieza del <strong>{r.current_date}</strong> → mover a <strong>{r.move_to_date}</strong></span>
                            <span className="text-[#5C5E5C] flex-1">· {r.why}</span>
                          </div>
                        ))}
                      </div>
                      <div className="text-[10px] text-[#A69076] mt-3 italic">Por ahora la reorganización es solo sugerida — edita manualmente la fecha de cada slot que quieras mover.</div>
                    </div>
                  )}

                  {/* All pieces with scores */}
                  <div className="bg-white rounded-2xl border border-[#E6E2DC] p-5" data-testid="monthly-all-pieces-score">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-3">Todas las piezas evaluadas</div>
                    <div className="grid grid-cols-3 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
                      {evaluation.pieces.map((p) => {
                        const thumb = p.thumb_url ? (p.thumb_url.startsWith("http") ? p.thumb_url : `${BACKEND}${p.thumb_url}`) : null;
                        const goodScore = p.score >= 8;
                        const midScore = p.score >= 6 && p.score < 8;
                        return (
                          <div key={p.slot_id || p.index} className="relative aspect-square rounded-md overflow-hidden" title={`${p.score}/10 · ${p.reason}`}>
                            {thumb ? (
                              <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                              <div className="w-full h-full bg-[#F4F0EB]" />
                            )}
                            <div className={`absolute bottom-0 left-0 right-0 px-1 py-0.5 text-[9px] font-bold text-white ${goodScore ? "bg-[#7A9180]" : midScore ? "bg-[#C99776]" : "bg-[#D97757]"}`}>
                              {p.score}/10
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {active.story_ideas?.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#E6E2DC] p-6" data-testid="monthly-stories">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-2">Stories del mes · ideas para disparar desde el celular</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {active.story_ideas.map((idea, i) => (
                  <div key={i} className="rounded-lg bg-[#F4F0EB] px-3 py-2 text-[12px] text-[#3C3E3C] leading-snug">
                    <span className="text-[10px] font-bold text-[#A69076] mr-2">#{i + 1}</span>{idea}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Slot edit dialog */}
      <Dialog open={!!editingSlot} onOpenChange={(open) => !open && setEditingSlot(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSlot && (
                <span className="flex items-center gap-2">
                  {(() => {
                    const meta = TYPE_META[editingSlot.type];
                    const Icon = meta?.icon || ImageIcon;
                    return <Icon className="w-4 h-4" style={{ color: meta?.color }} />;
                  })()}
                  {editingSlot && `${TYPE_META[editingSlot.type]?.label} · ${editingSlot.date}`}
                  {editingSlot && editingSlot.status === "ready" && <CheckCircle2 className="w-4 h-4 text-[#7A9180]" />}
                  {editingSlot && editingSlot.status === "scheduled" && <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A1C1A] bg-[#D9CDBC] px-2 py-0.5 rounded">Programado</span>}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {editingSlot && (
            <div className="grid md:grid-cols-2 gap-5">
              {/* LEFT — preview */}
              <div className="space-y-3">
                {(() => {
                  const s = editingSlot;
                  let thumbSrc = null;
                  if (s.type === "reel" && s.thumb_url) {
                    thumbSrc = s.thumb_url.startsWith("http") ? s.thumb_url : `${BACKEND}${s.thumb_url}`;
                  } else if (s.thumb_inline) {
                    thumbSrc = s.thumb_inline.startsWith("data:") ? s.thumb_inline : `data:image/png;base64,${s.thumb_inline}`;
                  } else if (s.generated_id && (s.type === "post" || s.type === "carousel")) {
                    const idx = s.type === "carousel" ? "/slide/0" : "";
                    thumbSrc = `${BACKEND}/api/public/media/social/${s.generated_id}${idx}`;
                  }
                  const videoSrc = s.video_url ? (s.video_url.startsWith("http") ? s.video_url : `${BACKEND}${s.video_url}`) : null;

                  if (s.status === "generating") {
                    return (
                      <div className="aspect-square bg-[#F4F0EB] rounded-xl flex flex-col items-center justify-center gap-3 border border-[#E6E2DC]">
                        <Loader2 className="w-8 h-8 text-[#A69076] animate-spin" />
                        <div className="text-[11px] tracking-[0.2em] uppercase text-[#A69076]">Generando…</div>
                        <div className="w-2/3 h-1.5 bg-[#EFEAE2] rounded-full overflow-hidden">
                          <div className="h-full bg-[#A69076] transition-all" style={{ width: `${s.progress || 30}%` }} />
                        </div>
                        <div className="text-[10px] text-[#5C5E5C]">{s.progress || 30}%</div>
                      </div>
                    );
                  }
                  if (s.type === "reel" && videoSrc) {
                    return <video src={videoSrc} controls poster={thumbSrc || undefined} className="w-full aspect-[9/16] rounded-xl bg-black object-contain" data-testid="monthly-slot-video" />;
                  }
                  if (thumbSrc) {
                    return <img src={thumbSrc} alt="" className="w-full aspect-square rounded-xl object-cover border border-[#E6E2DC]" data-testid="monthly-slot-thumb" />;
                  }
                  return (
                    <div className="aspect-square bg-[#F4F0EB] rounded-xl flex items-center justify-center text-[#A69076] text-xs uppercase tracking-[0.2em] border border-[#E6E2DC]">
                      Sin generar
                    </div>
                  );
                })()}
                {editingSlot.slides_count > 1 && (
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#A69076]">{editingSlot.slides_count} slides · primer slide mostrado</div>
                )}
                {editingSlot.publish_error && (
                  <div className="text-[11px] text-[#7a3a3a] bg-[#F5E0E0] rounded px-3 py-2">Error: {editingSlot.publish_error}</div>
                )}
              </div>

              {/* RIGHT — meta + caption editor */}
              <div className="space-y-3">
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-1.5 block">Tema · gancho</Label>
                  <Textarea rows={2} value={editingSlot.topic} onChange={(e) => setEditingSlot({ ...editingSlot, topic: e.target.value })} data-testid="monthly-slot-topic" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-1.5 block">Hora local</Label>
                    <Input value={editingSlot.scheduled_time} onChange={(e) => setEditingSlot({ ...editingSlot, scheduled_time: e.target.value })} placeholder="HH:MM" data-testid="monthly-slot-time" />
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-1.5 block">Estado</Label>
                    <div className="h-9 flex items-center text-[11px] text-[#1A1C1A] capitalize">{editingSlot.status || "empty"}</div>
                  </div>
                </div>
                {editingSlot.generated_id && (
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-1.5 block">Caption editable</Label>
                    <Textarea
                      rows={6}
                      value={editingSlot.caption_preview || ""}
                      onChange={(e) => setEditingSlot({ ...editingSlot, caption_preview: e.target.value, _caption_dirty: true })}
                      placeholder="Caption del post / reel"
                      data-testid="monthly-slot-caption"
                    />
                    {editingSlot.hashtags_count > 0 && (
                      <div className="text-[10px] text-[#A69076] mt-1">{editingSlot.hashtags_count} hashtags</div>
                    )}
                  </div>
                )}
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-1.5 block">Notas internas</Label>
                  <Textarea rows={2} value={editingSlot.notes || ""} onChange={(e) => setEditingSlot({ ...editingSlot, notes: e.target.value })} data-testid="monthly-slot-notes" />
                </div>
              </div>

              {/* FULL-WIDTH actions */}
              <div className="md:col-span-2 flex gap-2 justify-end flex-wrap border-t border-[#EFEAE2] pt-4">
                <Button variant="outline" onClick={() => setEditingSlot(null)} data-testid="monthly-slot-cancel">Cancelar</Button>
                <Button onClick={saveSlot} className="bg-[#A69076] hover:bg-[#1A1C1A] text-white" data-testid="monthly-slot-save">Guardar cambios</Button>
                {editingSlot.topic && editingSlot.status === "empty" && (
                  <Button onClick={() => { generateSlot(editingSlot.slot_id); setEditingSlot(null); }} className="bg-[#1A1C1A] hover:bg-[#A69076] text-white" data-testid="monthly-slot-generate">
                    <Sparkles className="w-4 h-4 mr-1" /> Generar
                  </Button>
                )}
                {editingSlot.status !== "empty" && editingSlot.status !== "generating" && (
                  <Button onClick={() => { generateSlot(editingSlot.slot_id); setEditingSlot(null); }} variant="outline" data-testid="monthly-slot-regenerate">
                    <RefreshCw className="w-4 h-4 mr-1" /> Regenerar
                  </Button>
                )}
                {editingSlot.status === "ready" && (
                  <Button onClick={() => approveSlot(editingSlot)} className="bg-[#7A9180] hover:bg-[#1A1C1A] text-white" data-testid="monthly-slot-approve">
                    <Send className="w-4 h-4 mr-1" /> Aprobar y programar
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
